/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useMemo, useState } from 'react';
import { ActiveSubLevelItem, ImpressionTrackingType, LinksActive, MainLink } from '../../../types';
import {
  compareUrlLastSegments,
  getElementsByClass,
  getItemHref,
  getItemTitle,
  getMainItemHref,
  getSubLevelChildLabel,
  getTopLevelActiveItemHref,
  hasNavigationSubLinks,
  hasSubLinks,
  isSmallScreen,
  SECOND_LEVEL_LINK_INDEX,
} from '../../utils/utils';
import { useInPageDispatch, useInPageNavContext } from '../../context';
import { INPAGENAV_ACTIONS } from '../../reducers/inPageNavReducer';

const CLASS_NAME = 'inpage-nav-menu';
const TOP_LEVEL_LINKS_CLASS = 'top-level-link';
const TOP_LEVEL_LABEL_CLASS = 'top-level-label';
const TOP_LEVEL_LINK_INDEX = 'top-link';

const useActiveLink = (
  setIsSublinksActiveUrl: (setIsSublinksActiveUrl: boolean) => void,
  navSelector: string = `nav.${CLASS_NAME} a`,
  activeClass: string = 'active-link',
): void => {
  const dispatch = useInPageDispatch();
  const {
    accordionLabel,
    parentPageInpageNavigation,
    sameLevelInpageNavigation,
    sameLevelActiveLink,
    secondLevelActiveLink,
    priorityNavLoaded,
    topLevelActiveLinks,
    secondLevelActiveLinks,
  } = useInPageNavContext();
  const [activeSubLevelItems, setActiveSubLevelItems] = useState<ActiveSubLevelItem[]>([]);
  const [topLevelLinksActive, setTopLevelLinksActive] = useState<LinksActive[]>([]);
  const [secondLevelLinksActive, setSecondLevelLinksActive] = useState<LinksActive[]>([]);

  // intActiveItems use to count each time a non parent page state is updated to help determine default impression tracking
  const [intActiveItems, setIntActiveItems] = useState(0);
  // 'checkDefaultImpression' use to set default impression tracking if on top main parent page or landing page with no parent overview is active
  const [checkDefaultImpression, setcheckDefaultImpression] = useState(false);

  const checkSubLinks = useMemo(
    () => hasNavigationSubLinks(sameLevelInpageNavigation),
    [sameLevelInpageNavigation],
  );

  // First check top level links
  useEffect(() => {
    const currentPath = typeof window !== 'undefined' ? window?.location?.pathname : '';
    const activeItems: ActiveSubLevelItem[] = [];
    if (sameLevelInpageNavigation && sameLevelInpageNavigation?.length > 0) {
      sameLevelInpageNavigation?.forEach((item, i) => {
        const href = getMainItemHref(item.fields.href);

        const isComparedUrl = compareUrlLastSegments(currentPath, href);

        if (isComparedUrl) {
          const url = getTopLevelActiveItemHref(sameLevelInpageNavigation, i);
          setTopLevelLinksActive((prev) => [
            ...prev,
            {
              index: i,
              url,
            },
          ]);

          if (dispatch) {
            const text = getItemTitle(item.fields);
            dispatch({
              type: INPAGENAV_ACTIONS.SAME_LEVEL_ACTIVE_LINK,
              payload: {
                sameLevelActiveLink: {
                  id: item.id,
                  index: i,
                  text,
                  subLevelChildLabel: getSubLevelChildLabel(item.fields),
                  href: url,
                  // @ts-expect-error
                  target: item.fields.Target as string | undefined,
                },
                impressionTracking: {
                  type: ImpressionTrackingType.SAMELEVEL,
                  label: text,
                  value: checkSubLinks ? '1 sub level' : 'same level',
                },
                isLoadedFromUrl: true,
              },
            });
            setIntActiveItems((prev) => prev + 1);
          }
        }

        // populate second level links
        const subLinks: MainLink[] = hasSubLinks(i, sameLevelInpageNavigation);
        const isSubLinks = subLinks.length > 0;
        if (isSubLinks) {
          const activeSubLevelItem: ActiveSubLevelItem = {
            id: item.id,
            parentLabel: getItemTitle(item.fields),
            subLevelChildLabel: getSubLevelChildLabel(item.fields),
            index: i,
            subLevelInpageNavigation: subLinks,
          };
          activeItems.push(activeSubLevelItem);
        }
      });

      setActiveSubLevelItems(activeItems);
    }
  }, [priorityNavLoaded, navSelector, activeClass]);

  useEffect(() => {
    if (
      sameLevelActiveLink &&
      (sameLevelActiveLink?.subLevelChildLabel || sameLevelActiveLink?.text)
    ) {
      setIsSublinksActiveUrl(true);
    }
  }, [sameLevelActiveLink]);

  // Now check if url matches any second level links
  useEffect(() => {
    if (activeSubLevelItems?.length > 0) {
      const currentPath = typeof window !== 'undefined' ? window?.location?.pathname : '';
      activeSubLevelItems.forEach((subitem: ActiveSubLevelItem) => {
        const overviewLabel = subitem.subLevelChildLabel || subitem.parentLabel;
        subitem.subLevelInpageNavigation?.forEach((item, ii) => {
          const href = getItemHref(item.fields.href);
          const isComparedUrl = compareUrlLastSegments(currentPath, href);

          if (isComparedUrl) {
            // Set to true to activate sublevel toggle function
            setIsSublinksActiveUrl(true);
            if (dispatch) {
              const url = getTopLevelActiveItemHref(sameLevelInpageNavigation, subitem.index);

              setTopLevelLinksActive([
                {
                  index: Number(subitem.index),
                  url,
                },
              ]);

              dispatch({
                type: INPAGENAV_ACTIONS.TOP_LEVEL_ACTIVE_LINK,
                payload: {
                  topLevelActiveLink: {
                    id: subitem.id,
                    index: subitem.index,
                    text: subitem.parentLabel,
                    subLevelChildLabel: overviewLabel,
                    href: url,
                    target: item.fields.Target as string | undefined,
                  },
                  impressionTracking: {
                    type: ImpressionTrackingType.TOPLEVEL,
                    label: subitem.parentLabel,
                    value: checkSubLinks ? '1 sub level' : 'same level',
                  },
                },
              });
              setIntActiveItems((prev) => prev + 1);

              const urlSecond = getTopLevelActiveItemHref(subitem.subLevelInpageNavigation, ii);

              setSecondLevelLinksActive([
                {
                  index: Number(subitem.index),
                  url: urlSecond,
                },
              ]);

              dispatch({
                type: INPAGENAV_ACTIONS.SUB_LEVEL_ACTIVE_LINK,
                payload: {
                  secondLevelActiveLink: {
                    id: item.id,
                    index: subitem.index,
                    text: item.fields.Text,
                    href: urlSecond,
                    parentLabel: overviewLabel,
                    urlIndex: ii,
                    target: item.fields.Target as string | undefined,
                    overviewLinkSelected: false,
                  },
                },
              });

              dispatch({
                type: INPAGENAV_ACTIONS.IS_LOADED_FROM_URL,
                payload: {
                  isLoadedFromUrl: true,
                },
              });
            }
          }
        });
        setcheckDefaultImpression(true);
      });
    }
    if (!parentPageInpageNavigation) setcheckDefaultImpression(true);
  }, [activeSubLevelItems]);

  useEffect(() => {
    if (topLevelLinksActive?.length > 0) {
      if (dispatch) {
        dispatch({
          type: INPAGENAV_ACTIONS.TOP_LEVEL_ACTIVE_LINKS,
          payload: {
            topLevelActiveLinks: topLevelLinksActive,
          },
        });
      }
    }

    if (secondLevelLinksActive?.length > 0) {
      if (dispatch) {
        dispatch({
          type: INPAGENAV_ACTIONS.SECOND_LEVEL_ACTIVE_LINKS,
          payload: {
            secondLevelActiveLinks: secondLevelLinksActive,
          },
        });
      }
    }
  }, [topLevelLinksActive, secondLevelLinksActive]);

  useEffect(() => {
    if (checkDefaultImpression && intActiveItems === 0) {
      if (dispatch) {
        dispatch({
          type: INPAGENAV_ACTIONS.IMPRESSION_TRACKING,
          payload: {
            impressionTracking: {
              type: ImpressionTrackingType.PARENT,
              label: `${isSmallScreen() ? `${accordionLabel || ''} ` : ''}${parentPageInpageNavigation?.fields.subLevelParentLabel || ''}`,
              value: checkSubLinks ? '1 sub level' : 'same level',
            },
          },
        });
      }
    }
  }, [checkDefaultImpression, intActiveItems]);

  // apply active class to top level links and top level labels
  useEffect(() => {
    if (topLevelActiveLinks?.length) {
      // find all top level links desktop
      const topNavLinks = Array.from(
        getElementsByClass(TOP_LEVEL_LINKS_CLASS) as HTMLCollectionOf<HTMLAnchorElement>,
      );
      // find all top level labels on mobile
      const topNavLabels = Array.from(
        getElementsByClass(TOP_LEVEL_LABEL_CLASS) as HTMLCollectionOf<HTMLSpanElement>,
      );

      topLevelActiveLinks.forEach(({ url }) => {
        [...topNavLinks, ...topNavLabels].forEach((element) => {
          const elemUrl = element.getAttribute('href') || element.dataset.href;
          if (elemUrl === url) {
            element.classList.add(activeClass);
          }
        });
      });
    }
  }, [topLevelActiveLinks]);

  // apply active class to second level links and second level labels
  useEffect(() => {
    if (sameLevelInpageNavigation?.length && secondLevelActiveLinks?.length) {
      const topLinksIndex = secondLevelActiveLink?.index as number;
      const secondNavLinks = Array.from(
        document.querySelectorAll<HTMLAnchorElement>(
          `.${SECOND_LEVEL_LINK_INDEX}-${topLinksIndex}`,
        ),
      );
      secondNavLinks.forEach((element) => {
        const elemUrl = element.getAttribute('href') || element.dataset.href;
        if (secondLevelActiveLinks.some(({ url }) => url === elemUrl)) {
          element.classList.add(activeClass);
          // fallback add active class to top level link incase 'topLevelActiveLinks' have no href attributes
          document
            .querySelector(`.${TOP_LEVEL_LINK_INDEX}-${topLinksIndex}`)
            ?.classList.add(activeClass);
        }
      });
    }
  }, [sameLevelInpageNavigation, secondLevelActiveLinks]);
};

export default useActiveLink;
